import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useWindowWidth } from "../hooks/useWindowWidth";
import RequireGroup from '../components/RequireGroup';
import {
  IconPackageImport,
  IconPackageExport,
  IconPackages,
  IconPackage,
} from "@tabler/icons-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DataTableEstoque = ({ title, verEstoque, verItens, novaEntrada, novaSaida }) => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  // Armazenamos entries como string para permitir o campo vazio
  const [entries, setEntries] = useState("8");
  const [categories, setCategories] = useState([]);
  // Estado para a página atual
  const [currentPage, setCurrentPage] = useState(1);
  // Estados para o modal
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768; // ou o breakpoint que preferir

  // Função para abrir o modal com os dados da movimentação
  const handleView = (item) => {
    setModalData(item);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditMode(true);
    setEditData(item);
    setModalData(item); // Ensure modalData is set for rendering the modal
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}estoque/items/edit/${editData.id}`, {
        method: 'PUT', // ou 'PATCH' se for o caso
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      if (!response.ok) {
        throw new Error(`Erro ao editar item: ${response.status}`);
      }
      // Após a edição, recarrega os dados da tabela
      await fetchEstoque();
      setShowModal(false);
      setEditMode(false);
      setEditData(null);
    } catch (error) {
      console.error("Erro ao salvar a edição do item:", error);
    }
  };

  // Função para fechar o modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalData(null);
    setEditMode(false);
    setEditData(null);
  };

  // Formatação de datas e horas
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // Parse the date string manually as local date
    const [year, month, day] = dateString.split("-");
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  };

  const formatHour = (timeString) => {
    if (!timeString) return "";
    const parts = timeString.split(":");
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return "";
  };

  const getFullDateTime = (item) => {
    return new Date(`${item.data_movimento}T${item.hora_movimento}`);
  };

  const fetchEstoque = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}estoque/items/list/`);
      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }
      const result = await response.json();
      // Ordena os registros alfabeticamente pela descrição
      const sortedData = result.sort((a, b) => {
        const descA = a.descricao ? a.descricao.toLowerCase() : "";
        const descB = b.descricao ? b.descricao.toLowerCase() : "";
        return descA.localeCompare(descB);
      });
      setData(sortedData);
    } catch (error) {
      console.error("Erro ao buscar registros do estoque:", error);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}estoque/itemcategorias/`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data);
        } else {
          console.error("Erro ao buscar categorias");
        }
      } catch (error) {
        console.error("Erro na requisição de categorias:", error);
      }
    };
    fetchCategories();
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchEstoque();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    // Sempre que a busca mudar, reseta para a página 1
    setCurrentPage(1);
  };

  // Atualiza o valor de entries sem convertê-lo imediatamente para número
  const handleEntriesChange = (e) => {
    setEntries(e.target.value);
    setCurrentPage(1);
  };

  // Filtra os registros com base na busca (aplicada à descrição do item)
  const filteredData = data.filter((item) => {
    const descricao = item.descricao ? item.descricao.toLowerCase() : "";
    return descricao.includes(search.toLowerCase());
  });

  // Converte entries para número, garantindo um valor mínimo de 1 (para evitar divisão por zero)
  const numericEntries = entries === "" ? 1 : Number(entries);

  // Cálculo da paginação
  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(totalRecords / numericEntries);
  const startIndex = (currentPage - 1) * numericEntries;
  const paginatedData = filteredData.slice(startIndex, startIndex + numericEntries);

  // Função para mudar a página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const safeValue = (value) => {
    if (value == null || (typeof value === "string" && value.trim() === "")) {
      return "-";
    }
    return value;
  };

  // Função para gerar os botões de paginação
  const renderPagination = () => {
    let pages = [];
    if (totalPages <= 7) {
      // Se o número total de páginas é pequeno, exibe todas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Sempre exibe a primeira página
      pages.push(1);
  
      // Define o intervalo de páginas ao redor da página atual
      let start = Math.max(currentPage - 1, 2);
      let end = Math.min(currentPage + 1, totalPages - 1);
  
      // Ajusta o intervalo se a página atual estiver perto do início ou do fim
      if (currentPage <= 3) {
        start = 2;
        end = 4;
      }
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
        end = totalPages - 1;
      }
  
      // Se houver um gap entre a primeira página e o início do intervalo, insere ellipsis
      if (start > 2) {
        pages.push("ellipsis");
      }
  
      // Adiciona as páginas do intervalo
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
  
      // Se houver um gap entre o final do intervalo e a última página, insere ellipsis
      if (end < totalPages - 1) {
        pages.push("ellipsis");
      }
  
      // Sempre exibe a última página
      pages.push(totalPages);
    }
  
    return pages.map((page, index) => {
      if (page === "ellipsis") {
        return (
          <li key={index} className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      } else {
        return (
          <li key={index} className={`page-item ${page === currentPage ? "active" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(page)}>
              {page}
            </button>
          </li>
        );
      }
    });
  };

  return (
    <div className="card">
      {/* Header com botões de ação */}
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="card-title">{title}</h3>
        
      </div>

      {/* Controles de registros e busca */}
      <div className="card-body border-bottom py-3">
        <div className="d-flex">
          <div className="text-secondary">
            Mostrar
            <div className="mx-2 d-inline-block">
              <input
                type="text"
                className="form-control form-control-sm"
                value={entries}
                onChange={handleEntriesChange}
                size="3"
                aria-label="Contador de registros"
              />
            </div>
            registros
          </div>
          <div className="ms-auto text-secondary">
            Procurar:
            <div className="ms-2 d-inline-block">
              <input
                type="text"
                className="form-control form-control-sm"
                value={search}
                onChange={handleSearchChange}
                aria-label="Procurar"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de registros */}
      <div className="table-responsive">
        <table className="table card-table table-vcenter text-nowrap datatable">
        <thead>
            <tr>
              <th className="w-1">
                <input
                  type="checkbox"
                  className="form-check-input m-0 align-middle"
                  aria-label="Select all items"
                />
              </th>
              <th>ID</th>
              <th>Descrição</th>
              <th>Tipo</th>
              <th>Categoria</th>
              <th>Data de Validade</th>
              <th>Perecível</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody className="table-tbody">
            {paginatedData.map((item) => {
              const formattedDate = formatDate(item.data_movimento);
              const formattedHour = formatHour(item.hora_movimento);
              return (
                <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input m-0 align-middle"
                        aria-label="Selecionar item"
                      />
                    </td>
                    <td>
                      <span className="text-secondary">{item.id}</span>
                    </td>
                    <td style={{ textTransform: "uppercase" }}>
                      {item.descricao ? item.descricao : "-"}
                    </td>
                    <td style={{ textTransform: "uppercase" }}>
                      {item.tipo_item ? item.tipo_item : "-"}
                    </td>
                    <td style={{ textTransform: "uppercase" }}>
                      {item.categoria ? item.categoria : "-"}
                    </td>
                    <td>{formatDate(item.data_validade)}</td>
                    <td>{item.perecivel ? "Sim" : "Não"}</td>
                    <td></td>
                    <td className="text-end">
                      <span className="dropdown">
                        <button
                          className="btn dropdown-toggle align-text-top"
                          data-bs-toggle="dropdown"
                        >
                          Ações
                        </button>
                        <div className="dropdown-menu dropdown-menu-end">
                          <button className="dropdown-item" onClick={() => handleView(item)}>
                            Ver
                          </button>
                          <button className="dropdown-item" onClick={() => handleEdit(item)}>
                            Editar
                          </button>
                        </div>
                      </span>
                    </td>
                  </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rodapé com paginação */}
      <div className="card-footer d-flex align-items-center justify-content-between">
        <p className="m-0 text-secondary">
          Exibindo{" "}
          <span>{(currentPage - 1) * numericEntries + 1}</span> até{" "}
          <span>{Math.min(currentPage * numericEntries, totalRecords)}</span> de{" "}
          <span>{totalRecords}</span> registros
        </p>
        <ul className="pagination m-0">
          <li className={`page-item ${currentPage === 1 && "disabled"}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
              ante
            </button>
          </li>
          {renderPagination()}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}> 
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
              prox
            </button>
          </li>
        </ul>
      </div>

      {/* Modal para exibir informações da movimentação */}
      {showModal && modalData && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Detalhes do Item</h5>
                  <button type="button" className="btn btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  {!editMode ? (
                    <div>
                      <div className="text-reset d-block mt-2">
                        <strong>ID</strong>
                      </div>
                      <div className="d-block text-secondary text-truncate mt-n1">
                        {modalData.id}
                      </div>
                      <div className="text-reset d-block mt-2">
                        <strong>Item</strong>
                      </div>
                      <div className="d-block text-secondary text-truncate mt-n1">
                        {modalData.descricao ? modalData.descricao.toUpperCase() : "N/A"}
                      </div>
                      <div className="text-reset d-block mt-2">
                        <strong>Descrição</strong>
                      </div>
                      <div className="d-block text-secondary mt-n1 wrap-text">
                        {modalData.descricao_longa ? modalData.descricao_longa.toUpperCase() : "N/A"}
                      </div>
                      <div className="text-reset d-block mt-2">
                        <strong>Tipo</strong>
                      </div>
                      <div className="d-block text-secondary text-truncate mt-n1">
                        {modalData.tipo_item ? modalData.tipo_item.toUpperCase() : "N/A"}
                      </div>
                      <div className="text-reset d-block mt-2">
                        <strong>Categoria</strong>
                      </div>
                      <div className="d-block text-secondary text-truncate mt-n1">
                        {modalData.categoria ? modalData.categoria.toUpperCase() : "N/A"}
                      </div>
                      {(modalData.data_validade || modalData.perecivel) && (
                        <>
                          <div className="text-reset d-block mt-2">
                            <strong>Data de Validade</strong>
                          </div>
                          <div className="d-block text-secondary text-truncate mt-n1">
                            {formatDate(modalData.data_validade)}
                          </div>
                        </>
                      )}
                      <div className="text-reset d-block mt-2">
                        <strong>Perecível</strong>
                      </div>
                      <div className="d-block text-secondary text-truncate mt-n1">
                        {modalData.perecivel ? "Sim" : "Não"}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-3">
                        <label className="form-label"><strong>ID</strong></label>
                        <p className="form-control-plaintext">{editData.id}</p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label"><strong>Item</strong></label>
                        <input
                          type="text"
                          className="form-control"
                          value={editData.descricao || ""}
                          onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label"><strong>Descrição</strong></label>
                        <textarea
                          className="form-control"
                          value={editData.descricao_longa || ""}
                          onChange={(e) => setEditData({ ...editData, descricao_longa: e.target.value })}
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label"><strong>Tipo</strong></label>
                        <input
                          type="text"
                          className="form-control"
                          value={editData.tipo_item || ""}
                          onChange={(e) => setEditData({ ...editData, tipo_item: e.target.value })}
                        />
                      </div>
                      <user__selection>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Categoria</label>
                            <select
                              className="form-select"
                              name="categoria"
                              onChange={handleChange}
                              value={editData.categoria || ""}
                            >
                              <option value="">Selecione</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.nome}>
                                  {cat.nome.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </user__selection>
                      <div className="mb-3">
                        <label className="form-label"><strong>Data de Validade</strong></label>
                        <input
                          type="date"
                          className="form-control"
                          value={editData.data_validade || ""}
                          onChange={(e) => setEditData({ ...editData, data_validade: e.target.value })}
                        />
                      </div>
                      <div className="mb-3 form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="perecivelCheck"
                          checked={!!editData.perecivel}
                          onChange={(e) => setEditData({ ...editData, perecivel: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="perecivelCheck"><strong>Perecível</strong></label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {!editMode ? (
                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                      Fechar
                    </button>
                  ) : (
                    <>
                      <button type="button" className="btn btn-primary" onClick={handleSave}>
                        Salvar
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                        Cancelar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default DataTableEstoque;